import { z } from 'zod';

/**
 * Beleg als Mail-Entwurf (ADR 0113 + Nachtrag): ProERP schickt die fertige Mail mit
 * dem Beleg-PDF an einen Power-Automate-Flow, der sie über Microsoft Graph als Entwurf
 * in ein Postfach legt (nichts wird gesendet). Ein Flow und ein Postfach je Mandant —
 * gemeinsam für Angebotskalkulation, Verkauf und Einkauf. Die Flow-URL ist ein
 * Geheimnis: Sie geht nur hinein, nie hinaus (die API meldet nur, ob eine hinterlegt ist).
 */

/** Graph nimmt Anhänge in einem JSON-Aufruf bis ~4 MB (Base64 inklusive) — mit Luft. */
export const MAIL_DRAFT_MAX_ATTACHMENT_BYTES = 3 * 1024 * 1024;

const flowUrlSchema = z
  .string()
  .trim()
  .url('Flow-URL: vollständige Adresse erwartet')
  .max(2000)
  .refine((value) => value.startsWith('https://'), 'Flow-URL muss mit https:// beginnen');

/** Einrichtung (Lesen): nie die URL, nur ob sie da ist und ihr Host. */
export const mailDraftSettingsSchema = z.object({
  isConfigured: z.boolean(),
  /** Nur der Host der URL zur Kontrolle (z. B. „prod-12.westeurope.logic.azure.com"). */
  flowHost: z.string().nullable(),
  mailbox: z.string().nullable(),
  updatedAt: z.string().datetime().nullable(),
});
export type MailDraftSettings = z.infer<typeof mailDraftSettingsSchema>;

export const saveMailDraftSettingsSchema = z.object({
  /** Weglassen = unverändert, null = entfernen. */
  flowUrl: flowUrlSchema.nullable().optional(),
  mailbox: z.string().trim().email('Postfach: E-Mail-Adresse erwartet').max(200),
});
export type SaveMailDraftSettingsInput = z.infer<typeof saveMailDraftSettingsSchema>;

/** Für die Beleg-Editoren: Ist der Entwurf möglich, und in welches Postfach? */
export const mailDraftStatusSchema = z.object({
  isConfigured: z.boolean(),
  mailbox: z.string().nullable(),
});
export type MailDraftStatus = z.infer<typeof mailDraftStatusSchema>;

export const mailRecipientsSchema = z.array(z.string().trim().email('Empfänger: E-Mail-Adresse erwartet').max(200)).max(20);

/** Verkaufsbeleg/Bestellung als Mail-Entwurf: das PDF erzeugt der Server selbst. */
export const createDocumentMailDraftSchema = z.object({
  to: mailRecipientsSchema.min(1, 'Mindestens ein Empfänger'),
  cc: mailRecipientsSchema.default([]),
  subject: z.string().trim().min(1, 'Betreff fehlt').max(250),
  /** Text ohne HTML; Absätze mit Leerzeile. */
  text: z.string().max(20_000),
});
export type CreateDocumentMailDraftInput = z.infer<typeof createDocumentMailDraftSchema>;

export const mailDraftResultSchema = z.object({
  mailbox: z.string(),
  /** Link zum Entwurf in Outlook im Web (wenn der Flow ihn zurückgibt). */
  webLink: z.string().nullable(),
  fileName: z.string(),
});
export type MailDraftResult = z.infer<typeof mailDraftResultSchema>;

const escapeHtml = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Klartext → einfaches HTML für Outlook (Absätze, Zeilenumbrüche). */
export function mailTextToHtml(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .trim()
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/** Nachricht für Microsoft Graph (POST /users/{postfach}/messages legt einen Entwurf an). */
export function graphDraftMessage(input: {
  to: readonly string[];
  cc: readonly string[];
  subject: string;
  text: string;
  attachment: { name: string; contentBase64: string };
}) {
  const recipients = (addresses: readonly string[]) => addresses.map((address) => ({ emailAddress: { address } }));
  return {
    subject: input.subject,
    body: { contentType: 'HTML', content: mailTextToHtml(input.text) },
    toRecipients: recipients(input.to),
    ccRecipients: recipients(input.cc),
    attachments: [
      { '@odata.type': '#microsoft.graph.fileAttachment', name: input.attachment.name, contentType: 'application/pdf', contentBytes: input.attachment.contentBase64 },
    ],
  };
}

export type MailLanguage = 'DE' | 'EN';

const isGermanSalutation = (value: string) => /^(sehr geehrte|liebe|hallo|guten tag|grüß)/i.test(value);
const isEnglishSalutation = (value: string) => /^(dear|hello|hi)\b/i.test(value);
const withComma = (value: string) => (/[,!:]$/.test(value) ? value : `${value},`);

/**
 * Anrede für einen Ansprechpartner aus dem CRM, in der Sprache des Belegs:
 * gepflegte Briefanrede („Sehr geehrte Frau Dr. Muster,") hat Vorrang, sofern sie zur
 * Sprache passt; sonst aus Geschlecht, Titel und Nachname; sonst die neutrale Anrede.
 */
export function contactLetterSalutation(
  contact: { salutation?: string | null; title?: string | null; lastName?: string | null; gender?: string | null } | null | undefined,
  language: MailLanguage,
): string {
  const neutral = language === 'EN' ? 'Dear Sir or Madam,' : 'Sehr geehrte Damen und Herren,';
  if (!contact) return neutral;
  const stored = contact.salutation?.trim();
  if (stored) {
    const fits = language === 'EN' ? !isGermanSalutation(stored) : !isEnglishSalutation(stored);
    if (fits) return withComma(stored);
  }
  const lastName = contact.lastName?.trim();
  const gender = contact.gender?.trim().toLowerCase() ?? '';
  const isFemale = /^(w|f|weiblich|female|frau)/.test(gender);
  const isMale = /^(m|männlich|male|herr)/.test(gender);
  if (!lastName || (!isFemale && !isMale)) return neutral;
  const title = contact.title?.trim();
  // Englisch: „Dear Dr Muster" statt „Dear Ms Dr. Muster"; andere (deutsche) Titel entfallen.
  if (language === 'EN') return /\bdr\b/i.test(title ?? '') ? `Dear Dr ${lastName},` : `Dear ${isFemale ? 'Ms' : 'Mr'} ${lastName},`;
  const name = [title, lastName].filter(Boolean).join(' ');
  return isFemale ? `Sehr geehrte Frau ${name},` : `Sehr geehrter Herr ${name},`;
}

/**
 * Vorschlag für den Mailtext zu einem Beleg (Verkauf/Einkauf) in der Belegsprache.
 * `title` ist Belegart + Nummer („Angebot AN-2610007"), `salutation` die Anrede aus
 * {@link contactLetterSalutation} (ohne: neutral).
 */
export function documentMailText(input: { title: string; language: MailLanguage; senderName?: string | null; salutation?: string | null }): string {
  const sender = input.senderName?.trim();
  if (input.language === 'EN') {
    const greeting = input.salutation?.trim() || 'Dear Sir or Madam,';
    return `${greeting}\n\nplease find attached our ${input.title}.\n\nPlease do not hesitate to contact us if you have any questions.\n\nKind regards${sender ? `\n${sender}` : ''}`;
  }
  const greeting = input.salutation?.trim() || 'Sehr geehrte Damen und Herren,';
  return `${greeting}\n\nanbei erhalten Sie unser${titleEnding(input.title)} ${input.title}.\n\nFür Rückfragen stehen wir gerne zur Verfügung.\n\nMit freundlichen Grüßen${sender ? `\n${sender}` : ''}`;
}

/**
 * „unser Angebot", „unseren Auftrag"? — Akkusativ je Belegart. Feminin (Rechnung,
 * Bestellung, Anfrage …) → „unsere", maskulin (Lieferschein, Auftrag) → „unseren",
 * sonst neutral „unser".
 */
function titleEnding(title: string): string {
  const word = title.trim().split(/\s+/)[0]?.toLowerCase() ?? '';
  if (/(rechnung|bestellung|anfrage|gutschrift|bestätigung|liste)$/.test(word)) return 'e';
  if (/(lieferschein|auftrag)$/.test(word)) return 'en';
  return '';
}
